(() => {
  if (!document.body.classList.contains('brio-product-page')) return;

  const flavourPaths = new Set([
    '/products/brio-original',
    '/products/gingembre',
    '/products/fraise-citron-vert',
    '/products/menthe-citron'
  ]);
  let currentPath = location.pathname;
  let pending = null;

  const isFlavour = (url) => url.origin === location.origin && flavourPaths.has(url.pathname.replace(/^\/(?:en|es|it)(?=\/)/, ''));
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const syncHead = (next) => {
    document.title = next.title;
    const selectors = [
      'link[rel="canonical"]',
      'link[rel="alternate"][hreflang]',
      'meta[name="description"]',
      'meta[property^="og:"]',
      'meta[name^="twitter:"]'
    ];
    selectors.forEach(selector => {
      document.head.querySelectorAll(selector).forEach(node => node.remove());
      next.head.querySelectorAll(selector).forEach(node => document.head.append(document.importNode(node, true)));
    });
  };

  const loadProductScripts = async (next) => {
    const scripts = [...next.head.querySelectorAll('script[src]')]
      .filter(script => /\/gsap-(?:3\.|scrolltrigger-)/.test(new URL(script.src).pathname));
    for (const script of scripts) {
      if (document.head.querySelector(`script[src="${script.src}"]`)) continue;
      await new Promise((resolve, reject) => {
        const loaded = document.createElement('script');
        loaded.src = script.src;
        loaded.onload = resolve;
        loaded.onerror = reject;
        document.head.append(loaded);
      });
    }
  };

  const preloadVisibleHero = async (nextMain) => {
    // The first visible gallery frame is the studio can. The photo is hidden.
    const source = nextMain.querySelector('.pdp-studio.is-active .pdp-studio-can')?.getAttribute('src');
    if (!source) return;
    const image = new Image();
    image.src = source;
    await image.decode();
  };

  const closeMenu = async () => {
    const menu = document.querySelector('.brio-mobile-menu[open]');
    if (!menu) return;
    menu.classList.add('brio-mobile-menu--leaving');
    await delay(160);
    menu.open = false;
    menu.classList.remove('brio-mobile-menu--leaving');
    menu.removeAttribute('aria-busy');
  };

  const reportProductView = (previousUrl, main) => {
    try {
      const product = main.querySelector('[data-pdp]');
      const data = JSON.parse(product?.querySelector('[data-pdp-data]')?.textContent || '{}');
      const variant = data.variants?.find(item => String(item.id) === String(data.selected)) || data.variants?.[0];
      const name = product?.querySelector('.pdp-buy h1')?.textContent?.trim() || document.title;
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_title: document.title,
          page_location: location.href,
          page_referrer: previousUrl
        });
        if (variant) window.gtag('event', 'view_item', {
          currency: data.currency || 'EUR',
          value: variant.price / 100,
          items: [{ item_id: String(variant.id), item_name: name, item_brand: 'BRIO', price: variant.price / 100 }]
        });
      }
      window.Shopify?.analytics?.publish?.('brio:product_navigation', {
        product: location.pathname,
        previous_url: previousUrl
      });
    } catch (_) { /* Analytics must never interrupt product navigation. */ }
  };

  const navigate = async (url, push) => {
    if (pending) pending.abort();
    const controller = new AbortController();
    pending = controller;
    const menu = document.querySelector('.brio-mobile-menu[open]');
    menu?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url.href, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const next = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextMain = next.querySelector('main#MainContent');
      if (next.documentElement.lang !== document.documentElement.lang || !next.body.classList.contains('brio-product-page') || !nextMain?.querySelector('[data-pdp]'))
        throw new Error('Unexpected product page');

      // Keep the old page on screen until the new hero image is fully decoded.
      await Promise.all([loadProductScripts(next), preloadVisibleHero(nextMain)]);
      if (controller.signal.aborted) return;

      const oldMain = document.querySelector('main#MainContent');
      const previousUrl = location.href;
      let importedMain;
      const swap = () => {
      oldMain.dispatchEvent(new Event('shopify:section:unload', { bubbles: true }));
      syncHead(next);
      if (push) history.pushState(null, '', url.href);
      importedMain = document.importNode(nextMain, true);
      oldMain.replaceWith(importedMain);
      currentPath = url.pathname;
      document.documentElement.style.scrollBehavior = 'auto';
      scrollTo(0, 0);
      requestAnimationFrame(() => { document.documentElement.style.scrollBehavior = ''; });
      importedMain.dispatchEvent(new Event('shopify:section:load', { bubbles: true }));
      reportProductView(previousUrl, importedMain);
      window.ScrollTrigger?.refresh();
      if (document.startViewTransition && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        if (menu) { menu.open = false; menu.classList.remove('brio-mobile-menu--leaving'); }
      }
      };
      if (document.startViewTransition && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        await document.startViewTransition(swap).finished.catch(() => {});
      } else { swap(); await closeMenu(); }
      importedMain.focus({ preventScroll: true });
    } catch (error) {
      if (!controller.signal.aborted) location.assign(url.href);
    } finally {
      if (pending === controller) pending = null;
      menu?.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', event => {
    const link = event.target.closest('.brio-mobile-flavours__grid a');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, location.href);
    if (!isFlavour(url)) return;
    event.preventDefault();
    if (url.pathname === currentPath) {
      closeMenu();
      return;
    }
    navigate(url, true);
  }, { capture: true });

  addEventListener('popstate', () => {
    const url = new URL(location.href);
    if (isFlavour(url) && url.pathname !== currentPath) navigate(url, false);
  });
})();
